import { createRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { rootRoute } from './__root';
import { profileKeys } from '@/features/settings/queries';
import { BillingPendingState } from '@/features/billing/BillingPendingState';
import { billingSearchSchema } from '@/features/billing/search-schema';
import { getProfile } from '@/lib/profile';
import { requireAuth } from '@/lib/route-guards';
import { ACCESS } from '@/features/access/access';
import { isRouteRoleAllowed } from '@/features/access/role-route-guard';

export const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing',
  beforeLoad: (ctx) => {
    requireAuth(ctx);
    // The Plans tab needs billing writes (OWNER): a blocked deep link lands on
    // Overview (the page itself is open to every role), same shape as `/settings`.
    if (ctx.search.tab !== 'plans') return;
    return isRouteRoleAllowed(ctx.context.queryClient, ACCESS.billingManage).then((allowed) => {
      if (!allowed) throw redirect({ to: '/billing', search: { tab: 'overview' }, replace: true });
    });
  },
  // Eagerly imported (not inside the lazy component) so a deep link's search
  // params are validated before the route chunk loads — same shape as
  // `/settings` and `/email-accounts`.
  validateSearch: billingSearchSchema,
  // Prime the profile cache before the component mounts so the owner-gating in
  // `BillingPage` is flash-free. Billing data (subscription / plans / invoices /
  // capabilities) self-fetches inside each card, exactly as it did in the
  // Organization tab. Swallow the error — the page renders its own states.
  loader: async ({ context: { queryClient } }) => {
    await queryClient
      .ensureQueryData({ queryKey: profileKeys.current(), queryFn: getProfile })
      .catch(() => undefined);
  },
  // `pendingMs: 0` / `pendingMinMs: 0` — no debounce, no artificial display
  // floor (see `routes/email-accounts.$id.tsx` for the precedent/rationale).
  pendingComponent: BillingPendingState,
  pendingMs: 0,
  pendingMinMs: 0,
  component: lazyRouteComponent(
    () => import('@/features/billing/BillingPage'),
    'BillingPage',
  ),
});
